import React, { useEffect } from 'react';
import { Col, Row, Spin } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams } from 'react-router-dom';
import ScopeThreeHeader from 'shared/components/Scope3Header';
import { ScopeThreeFormEditProvider } from 'context/ScopeThreeFormEditContext';
import { useScopeThreeCategoryUrlMode } from 'shared/hooks/useScopeThreeCategoryUrlMode';
import useRedirect from 'shared/utils/useRedirect';
import { ActivityDataEntryScopeTabs } from 'enums/ScopeThreeCategoryStatusEnums';
import { useYearLabel } from 'shared/utils/generateYearSpan';
import Scope3ManualEmissionService from 'services/Scope3ManualEmissionService/scope3ManualEmission.service';
import {
  getManualScope3CategoryConfig,
  ManualScope3Category,
} from 'constants/Scope3Constants/manualScope3Categories.constants';
import {
  Scope3ManualEmissionFormValues,
  Scope3ManualEmissionPayload,
} from 'models/Scope3/scope3ManualEmission.model';
import { manualEmissionValidationSchema } from './manualEmissionValidationSchema';
import Scope3ManualEmissionForm from './Scope3ManualEmissionForm';
import { MANUAL_SCOPE3_CATEGORY_ICONS } from './manualCategoryIcons';
import styles from './manualEmission.module.scss';

const emptyFormValues: Scope3ManualEmissionFormValues = {
  unit: null,
  emissions: null,
};

interface Scope3ManualEmissionPageProps {
  category: ManualScope3Category;
}

const Scope3ManualEmissionPage = ({
  category,
}: Scope3ManualEmissionPageProps) => {
  const { redirectToActivityDataEntry } = useRedirect();
  const { id: locationId } = useParams();
  const {
    adeParams,
    isEditView,
    enterEditMode,
    clearEditModeParams,
  } = useScopeThreeCategoryUrlMode();
  const { activeTab } = adeParams;
  const { loading, fetchManualEmission, updateManualEmission } =
    Scope3ManualEmissionService();

  const config = getManualScope3CategoryConfig(category);
  const CategoryIcon = MANUAL_SCOPE3_CATEGORY_ICONS[category];
  const backScopeTab =
    config?.section === 'downstream'
      ? ActivityDataEntryScopeTabs.SCOPE_3_DOWNSTREAM
      : ActivityDataEntryScopeTabs.SCOPE_3_UPSTREAM;
  const yearLabel = useYearLabel(activeTab);

  const methods = useForm<Scope3ManualEmissionFormValues>({
    defaultValues: emptyFormValues,
    resolver: yupResolver(manualEmissionValidationSchema),
    mode: 'onChange',
    shouldFocusError: false,
  });
  const { handleSubmit, reset } = methods;

  const loadManualEmission = async () => {
    if (!locationId || !activeTab) return;
    const values = await fetchManualEmission(locationId, activeTab, category);
    reset(values ?? emptyFormValues);
  };

  useEffect(() => {
    loadManualEmission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, activeTab, category]);

  const handleCancelEdit = async () => {
    await loadManualEmission();
    clearEditModeParams();
  };

  const onSubmit = async (values: Scope3ManualEmissionFormValues) => {
    if (!locationId || !activeTab || !values.unit) return;
    const payload: Scope3ManualEmissionPayload = {
      unit: values.unit,
      emissions: Number(values.emissions),
    };
    const response = await updateManualEmission(
      locationId,
      activeTab,
      category,
      payload
    );
    if (!response) return;
    redirectToActivityDataEntry(
      locationId,
      {
        activeTab,
        scopeTab: backScopeTab,
      },
      true
    );
  };

  if (!config || !CategoryIcon) return null;

  return (
    <ScopeThreeFormEditProvider isEditView={isEditView}>
      <div className={styles.pageWrapper}>
        <Spin spinning={loading} className={styles.loader}>
          <FormProvider {...methods}>
            <Row className={styles.contentContainer}>
              <Col span={24} className={styles.contentWrapper}>
                <ScopeThreeHeader
                  icon={<CategoryIcon />}
                  title={config.label}
                  year={yearLabel}
                  onSubmit={handleSubmit(onSubmit)}
                  loading={loading}
                  category={config.id}
                  isEditView={isEditView}
                  onEnterEdit={enterEditMode}
                  onCancelEdit={handleCancelEdit}
                  backScopeTab={backScopeTab}
                />
              </Col>
              <Col span={24}>
                <Scope3ManualEmissionForm
                  helpdeskKey={config.helpdeskKey}
                  readOnly={!isEditView}
                />
              </Col>
            </Row>
          </FormProvider>
        </Spin>
      </div>
    </ScopeThreeFormEditProvider>
  );
};

export default Scope3ManualEmissionPage;

export const Category2 = () => (
  <Scope3ManualEmissionPage category="category_2" />
);
export const Category3 = () => (
  <Scope3ManualEmissionPage category="category_3" />
);
export const Category7 = () => (
  <Scope3ManualEmissionPage category="category_7" />
);
export const Category8 = () => (
  <Scope3ManualEmissionPage category="category_8" />
);
export const Category10 = () => (
  <Scope3ManualEmissionPage category="category_10" />
);
export const Category11 = () => (
  <Scope3ManualEmissionPage category="category_11" />
);
export const Category12 = () => (
  <Scope3ManualEmissionPage category="category_12" />
);
export const Category13 = () => (
  <Scope3ManualEmissionPage category="category_13" />
);
export const Category14 = () => (
  <Scope3ManualEmissionPage category="category_14" />
);
export const Category15 = () => (
  <Scope3ManualEmissionPage category="category_15" />
);
